/**
 * Keeps the screen awake while a recording runs and while it is sent (UC-8, light, slice 02): a
 * phone that locks stops the recorder, and an upload with it. The browser lets the lock go
 * whenever the page is hidden, so it is taken again each time the page is visible. Where the
 * browser has no wake lock, or refuses one (a low battery), the screen says that it cannot keep
 * itself on.
 */
import { useEffect, useState } from 'react'
import type { LiveRecording } from '@/components/humint/questioning/recording-types'

/** Whether the browser keeps the screen awake, as the recording screen says it. */
export type ScreenWakeLock = LiveRecording['wakeLock']

const wakeLockOf = (): WakeLock | null =>
  typeof navigator !== 'undefined' && 'wakeLock' in navigator ? navigator.wakeLock : null

/** `held` while `active` and the browser keeps the screen on; `unsupported` when it cannot. */
export function useScreenWakeLock(active: boolean): ScreenWakeLock {
  const [state, setState] = useState<ScreenWakeLock>(() => (wakeLockOf() ? 'held' : 'unsupported'))

  useEffect(() => {
    const wakeLock = wakeLockOf()
    if (!active || !wakeLock) return
    let sentinel: WakeLockSentinel | null = null
    let over = false
    const take = async () => {
      try {
        const taken = await wakeLock.request('screen')
        // It was over before the browser answered.
        if (over) {
          void taken.release().catch(() => undefined)
          return
        }
        sentinel = taken
        setState('held')
      } catch {
        if (!over) setState('unsupported')
      }
    }
    const takeWhenVisible = () => {
      if (document.visibilityState === 'visible') void take()
    }
    void take()
    document.addEventListener('visibilitychange', takeWhenVisible)
    return () => {
      over = true
      document.removeEventListener('visibilitychange', takeWhenVisible)
      void sentinel?.release().catch(() => undefined)
    }
  }, [active])

  return state
}
